import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { VideoUploadModal } from '../components/VideoUploadModal';
import { supabase } from '../config/supabase';
import { Check, X as XIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const VideosContainer = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const VideosHeader = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleSection = styled.div``;

const VideosTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 1rem;
`;

const ContentContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const VideoList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
`;

const VideoRow = styled.div`
  display: flex;
  align-items: center;
  background: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 1rem;
  gap: 2rem;
  cursor: pointer;
  transition: transform 0.2s ease;
  flex-direction: column;
  flex-shrink: 1;
  &:hover {
    transform: translateY(-2px);
  }
`;

const Thumbnail = styled.img`
  width:100%;
  max-width: 350px;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
  background: ${colors.neutral.offWhite};
`;

const VideoInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Status = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
`;

export default function Videos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [videos, setVideos] = useState<any[]>([]);

  const fetchVideos = async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      // Step 1 & 2: Get the creator's ID from CreatorDetails using the auth user's ID
      const { data: creatorDetails, error: creatorError } = await supabase
        .from('CreatorDetails')
        .select('id')
        .eq('AuthID', user.id)
        .single();

      if (creatorError) {
        console.error('Error fetching creator details:', creatorError);
        setVideos([]);
        setLoading(false);
        return;
      }

      if (!creatorDetails) {
        console.error('No creator details found for user');
        setVideos([]);
        setLoading(false);
        return;
      }
      
      const creatorId = creatorDetails.id;

      // Step 3 & 4: Fetch videos using the creator's ID
      const { data, error } = await supabase
        .from('Videos')
        .select('*')
        .eq('CreatorID', creatorId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching videos:', error);
        setVideos([]);
      } else {
        setVideos(data || []);
      }
    } catch (error) {
      console.error('Error in fetchVideos:', error);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchVideos();
  }, [user]);

  return (
    <VideosContainer>
      <VideosHeader>
        <HeaderContent>
          <TitleSection>
            <VideosTitle>Videos</VideosTitle>
            <SubtitleMedium>Watch cooking tutorials and recipe demonstrations</SubtitleMedium>
          </TitleSection>
          <Button onClick={() => setIsUploadModalOpen(true)}>Upload Video</Button>
        </HeaderContent>
      </VideosHeader>
      <ContentContainer>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <VideoList>
            {videos.length === 0 ? (
              <div>No videos found.</div>
            ) : (
              videos.map(video => (
                <VideoRow 
                  key={video.VideoId}
                  onClick={() => navigate(`/videos/${video.VideoId}`)}
                >
                  <Thumbnail
                    src={
                      video.MuxPlaybackId
                        ? `https://image.mux.com/${video.MuxPlaybackId}/thumbnail.png?time=2`
                        : ''
                    }
                    alt="Video thumbnail"
                  />
                  <VideoInfo>
                    <div><b>Title:</b> {video.Title || 'Untitled'}</div>
                    <Status>
                      <b>Live:</b> {video.Live ? <Check color={colors.brand.laurel} /> : <XIcon color={colors.reds.bright} />}
                    </Status>
                    <Status>
                      <b>Free:</b> {video.Free ? <Check color={colors.brand.laurel} /> : <XIcon color={colors.reds.bright} />}
                    </Status>
                  </VideoInfo>
                </VideoRow>
              ))
            )}
          </VideoList>
        )}
      </ContentContainer>
      <VideoUploadModal 
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={fetchVideos}
      />
    </VideosContainer>
  );
} 